import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import "react-intl-tel-input-18/dist/main.css";
import parsePhoneNumber from "libphonenumber-js";

import Splide from "@coninsa-ui/react-splide";
import IntlPhone from "@coninsa-s2/intl-phone";
import { storage } from "@coninsa/utils";

import { CREATE_ADVERTISER_USER } from "../graphql/mutations";

type T_fields = {
  phone: string;
};

export default function Auth0({ onSuccess }) {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<T_fields>();
  const [mutation] = useMutation(CREATE_ADVERTISER_USER);

  const onSubmit = async (data: T_fields) => {
    const response = await mutation({ variables: { phone: data.phone } });
    const status = response?.data?.createAdvertiserUser?.status;

    if (status === "success") {
      storage.set("consignation.user", {
        cellphone: data.phone,
        isNew: response?.data?.createAdvertiserUser?.isNew,
      });

      onSuccess();
    }
  };

  return (
    <div>
      <div className="container py-4 md:py-6">
        <h2 className="mx-auto mb-6 text-center text-2xl font-medium leading-5 text-[var(--color-primary)] md:mb-12 md:w-[750px] md:text-[40px] md:leading-10">
          <span className="block text-center font-heading text-[34px] leading-8 text-[var(--color-secondary)] md:text-5xl md:leading-10">
            Recuerda tener a la mano
          </span>
          esta información
        </h2>

        <Splide
          options={{
            paddingRight: "60px",
            gap: 16,
            itemsToShow: 3,
          }}
        >
          <div className="co-numeric-card">
            <div className="co-numeric-card__number">1</div>
            <div className="co-numeric-card__content">
              <h4 className="co-heading-4 is-secondary">
                Información de inmueble
              </h4>
              <p className="text-sm md:text-base">
                Descripción, ubicación, detalles y características, entre otros.
              </p>
            </div>
          </div>
          <div className="co-numeric-card">
            <div className="co-numeric-card__number">2</div>
            <div className="co-numeric-card__content">
              <h4 className="co-heading-4 is-secondary">
                Datos del propietario
              </h4>
              <p className="text-sm md:text-base">
                Los datos básicos del propietario del inmueble.
              </p>
            </div>
          </div>
          <div className="co-numeric-card">
            <div className="co-numeric-card__number">3</div>
            <div className="co-numeric-card__content">
              <h4 className="co-heading-4 is-secondary">Fotografías</h4>
              <p className="text-sm md:text-base">
                Recuerda tener las fotografías del inmueble y aumentar la
                visibilidad.
              </p>
            </div>
          </div>
        </Splide>
      </div>
      <div className="co-card short-form-container">
        <h4 className="co-heading-4">Ingresa tu número móvil</h4>
        <p className="text-sm md:text-base">
          No dudamos que seas tu, pero te enviaremos un código de seguridad de 6
          dígitos a tu número de celular mediante mensaje de texto para
          confirmarlo y continuar.
        </p>

        <form className="co-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="form-control is-intl-tel">
            <Controller
              control={control}
              name="phone"
              rules={{
                validate: (value) =>
                  parsePhoneNumber(value)?.isValid() ||
                  "Número de teléfono inválido",
              }}
              render={({ field: { onChange, value } }) => (
                <IntlPhone
                  id="referrer_phone"
                  placeholder="Número de contacto"
                  defaultCountry={"co"}
                  onChange={onChange}
                  required
                  value={value}
                />
              )}
            />

            {errors?.phone && (
              <p className="form-error">{errors?.phone?.message}</p>
            )}

            {errors?.root?.type === "DUPLICATED_USER" && (
              <p className="mt-4 text-orange-600">
                El usuario ya ha sido registrado, por favor revisa tu teléfono
                para obtener tu código de verificación. Haga click{" "}
                <a onClick={onSuccess} className="cursor-pointer text-co-teal">
                  aquí
                </a>{" "}
                para continuar
              </p>
            )}
          </div>

          <div className="form-control is-checkbox">
            <input
              className="accent-s2-teal"
              type="checkbox"
              id="data-policy"
              name="data-policy"
              required
            />
            <label htmlFor="data-policy" className="!text-sm">
              Autorizo el tratamiento y la{" "}
              <a
                className="co-link text-s2-teal"
                href="https://www.coninsa.co/politica-de-tratamiento-de-datos-personales-de-coninsa"
                target="_blank"
              >
                Política de datos personales
              </a>{" "}
              y los{" "}
              <a
                className="co-link text-s2-teal"
                href="https://www.coninsa.co/terminos-y-condiciones"
                target="_blank"
              >
                Términos y condiciones
              </a>
            </label>
          </div>

          <button className="co-button is-progress is-large">
            Recibir código por mensaje de texto
          </button>
        </form>
      </div>
    </div>
  );
}
